import { AlertCircle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SceneState } from '@/lib/studio/studioState';
import { clock } from '@/lib/studio/format';
import { pickFocusScene, sceneWeights, timecode, type BuildScene } from '@/lib/studio/buildScenes';

function caption(scene: BuildScene, now: number): string {
  const head = `Scene ${scene.order}`;
  if (scene.status === 'built') return `${head} · Built`;
  if (scene.status === 'needs-attention') return `${head} · Needs attention`;
  if (scene.status === 'planned') return `${head} · Waiting to start`;
  const elapsed = scene.startedAt ? ` · building for ${clock(now - scene.startedAt)}` : '';
  return scene.drafts > 0 ? `${head} · Draft ${scene.drafts} saved${elapsed}` : `${head} · Building, no draft yet${elapsed}`;
}

function StatusMark({ status }: { status: SceneState['status'] }) {
  if (status === 'built') return <Check aria-label="Built" className="h-3.5 w-3.5 text-success" />;
  if (status === 'needs-attention') return <AlertCircle aria-label="Needs attention" className="h-3.5 w-3.5 text-destructive" />;
  if (status === 'building') return <span aria-label="Building" className="h-2 w-2 rounded-full bg-brand motion-safe:animate-pulse" />;
  return <span aria-label="Waiting" className="h-2 w-2 rounded-full border border-muted-foreground/60" />;
}

interface Props {
  scenes: BuildScene[];
  now: number;
  focusId?: string;
  onFocus?: (id: string) => void;
}

/** The video while it is being made: the focus scene's newest draft, every scene sized by its narration, a timeline. */
export function BuildView({ scenes, now, focusId, onFocus }: Props) {
  if (scenes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
        Scenes appear here once the plan is ready.
      </div>
    );
  }

  const focus = scenes.find((s) => s.id === focusId) ?? pickFocusScene(scenes)!;
  const weights = sceneWeights(scenes);
  const total = scenes.reduce((sum, s) => sum + (s.durationSec ?? 0), 0);

  return (
    <section aria-label="Scenes" className="flex h-full flex-col gap-4 overflow-auto p-4 sm:p-6">
      <figure data-testid="build-frame" className="relative mx-auto aspect-video w-full max-w-[960px] overflow-hidden rounded-xl border border-border bg-muted">
        {focus.thumbnail ? (
          <img src={focus.thumbnail} alt={`Scene ${focus.order}, newest draft`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full flex-col justify-center gap-2 p-6 sm:p-10">
            <span className="text-xs font-medium text-muted-foreground">From the plan</span>
            <p className="font-display text-lg font-semibold leading-snug text-foreground sm:text-2xl">{focus.title}</p>
            {focus.sketch && <p className="max-w-[60ch] text-sm leading-6 text-muted-foreground">{focus.sketch}</p>}
          </div>
        )}
        {focus.status === 'building' && focus.thumbnail && (
          <span className="absolute right-3 top-3 rounded-md bg-black/70 px-2 py-1 text-xs font-medium text-white">Draft</span>
        )}
        <figcaption className="absolute bottom-3 left-3 rounded-md bg-black/70 px-2.5 py-1.5 text-xs font-medium text-white">
          {caption(focus, now)}
        </figcaption>
      </figure>

      <div role="list" aria-label="Scenes" className="mx-auto flex w-full max-w-[960px] gap-1.5">
        {scenes.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="listitem"
            data-testid="scene-card"
            data-scene={s.id}
            aria-current={s.id === focus.id ? 'true' : undefined}
            onClick={() => onFocus?.(s.id)}
            style={{ flexGrow: weights[i], flexBasis: 0 }}
            className={cn(
              'group min-w-0 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <span
              className={cn(
                'relative block h-16 overflow-hidden rounded-lg border bg-muted sm:h-20',
                s.id === focus.id ? 'border-foreground ring-1 ring-foreground' : 'border-border group-hover:border-muted-foreground',
                !s.thumbnail && 'border-dashed',
              )}
            >
              {s.thumbnail ? (
                <img src={s.thumbnail} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="line-clamp-2 block p-2 text-[11px] leading-4 text-muted-foreground">{s.sketch || s.title}</span>
              )}
            </span>
            <span className="mt-1.5 flex items-center gap-1.5 text-xs">
              <StatusMark status={s.status} />
              <span className="min-w-0 flex-1 truncate text-foreground">{s.order} · {s.title}</span>
              {s.durationSec !== undefined && <span className="font-mono tabular-nums text-muted-foreground">{timecode(s.durationSec)}</span>}
            </span>
          </button>
        ))}
      </div>

      {total > 0 && (
        <div data-testid="timeline" className="mx-auto flex w-full max-w-[960px] justify-between font-mono text-[11px] tabular-nums text-muted-foreground">
          <span>0:00.0</span>
          <span>{timecode(total)}</span>
        </div>
      )}
    </section>
  );
}
